'use client';
import React, { useState, useEffect } from 'react';
import {
  IndianRupee,
  TrendingUp,
  ShoppingCart,
  Package,
  AlertTriangle,
  Star,
  Plus,
  Eye,
  Users,
  CreditCard,
  TrendingDown,
} from 'lucide-react';
import KPICard from '@/components/ui/kpi-card';
import SalesChart from '@/components/ui/sales-chart';
import { OnboardingDashboardWidget } from '@/components/onboarding/OnboardingDashboardWidget';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiService } from '@/lib/api';
import type { Payment, Item } from '@/lib/api';

interface DashboardStats {
  today: { sales: number; orders: number; growth: number };
  week: { sales: number; orders: number; growth: number };
  month: { sales: number; orders: number };
  year: { sales: number; orders: number };
  active_orders: number;
  low_stock_items: number;
  out_of_stock_items: number;
  total_items: number;
  total_stock_value: number;
  recent_products: number;
  total_customers: number;
  active_customers: number;
  new_customers_this_month: number;
  total_invoices: number;
  pending_invoices: number;
  paid_invoices: number;
  total_advances: number;
  active_advances: number;
  total_advance_amount: number;
  sales_trend: Array<{ date: string; sales: number; orders: number; customers: number }>;
  weekly_trend: Array<{ week: string; sales: number; orders: number }>;
  monthly_trend: Array<{ month: string; sales: number; orders: number }>;
  top_categories: Array<{ category__name: string; total_sales: number; item_count: number }>;
  top_customers: Array<{ customer__name: string; total_sales: number; invoice_count: number }>;
  payment_methods: Array<{ payment_method: string; total_amount: number; count: number }>;
  stock_alerts: Array<{ type: string; item_name: string; item_code: string; quantity: number; value: number }>;
  recent_activity: Array<{ type: string; title: string; subtitle: string; date: string; amount: number }>;
}

const Dashboard: React.FC = () => {
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const stats = await apiService.getDashboardStats();
      setDashboardStats(stats as DashboardStats);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      setDashboardStats(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnboardingAction = (action: string) => {
    switch (action) {
      case 'onboarding':
        window.location.href = '/onboarding';
        break;
      case 'products':
        window.location.href = '/products';
        break;
      case 'customers':
        window.location.href = '/customers';
        break;
      case 'registration':
      case 'verification':
      case 'setup':
        window.location.href = '/onboarding';
        break;
      default:
        console.log('Onboarding action:', action);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2 text-muted-foreground">Loading dashboard...</span>
          </div>
        </div>
      </div>
    );
  }

  // Use mock data when API fails
  const stats = dashboardStats || {
    today: { sales: 0, orders: 0, growth: 0 },
    week: { sales: 0, orders: 0, growth: 0 },
    month: { sales: 0, orders: 0 },
    year: { sales: 0, orders: 0 },
    active_orders: 0,
    low_stock_items: 0,
    out_of_stock_items: 0,
    total_items: 0,
    total_stock_value: 0,
    recent_products: 0,
    total_customers: 0,
    active_customers: 0,
    new_customers_this_month: 0,
    total_invoices: 0,
    pending_invoices: 0,
    paid_invoices: 0,
    total_advances: 0,
    active_advances: 0,
    total_advance_amount: 0,
    sales_trend: [],
    weekly_trend: [],
    monthly_trend: [],
    top_categories: [],
    top_customers: [],
    payment_methods: [],
    stock_alerts: [],
    recent_activity: []
  };

  // Ensure growth properties exist with default values
  const todayGrowth = stats.today?.growth ?? 0;
  const weekGrowth = stats.week?.growth ?? 0;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Onboarding Widget - Show at top if tenant is not complete */}
        <div className="mb-6">
          <OnboardingDashboardWidget onActionClick={handleOnboardingAction} />
        </div>

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KPICard
            title="Today's Sales"
            value={formatCurrency(stats.today.sales)}
            subtitle={`${stats.today.orders} orders`}
            icon={IndianRupee}
            trend={{ value: todayGrowth, isPositive: todayGrowth >= 0 }}
            variant="default"
          />
          <KPICard
            title="Weekly Revenue"
            value={formatCurrency(stats.week.sales)}
            subtitle={`${stats.week.orders} orders`}
            icon={TrendingUp}
            trend={{ value: weekGrowth, isPositive: weekGrowth >= 0 }}
          />
          <KPICard
            title="Active Orders"
            value={stats.active_orders.toString()}
            subtitle="Processing"
            icon={ShoppingCart}
          />
          <KPICard
            title="Low Stock"
            value={stats.low_stock_items.toString()}
            subtitle="Items need restock"
            icon={AlertTriangle}
            variant="destructive"
          />
        </div>

        {/* Additional KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KPICard
            title="Total Customers"
            value={stats.total_customers.toString()}
            subtitle={`${stats.active_customers} active`}
            icon={Users}
          />
          <KPICard
            title="Total Stock Value"
            value={formatCurrency(stats.total_stock_value)}
            subtitle={`${stats.total_items} items`}
            icon={Package}
          />
          <KPICard
            title="Advance Payments"
            value={formatCurrency(stats.total_advance_amount)}
            subtitle={`${stats.active_advances} active`}
            icon={CreditCard}
          />
          <KPICard
            title="Monthly Sales"
            value={formatCurrency(stats.month.sales)}
            subtitle={`${stats.month.orders} orders`}
            icon={TrendingUp}
          />
        </div>

        {/* Charts and Stats Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Sales Chart */}
          <div className="lg:col-span-2 bg-card rounded-lg p-6 border">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-card-foreground">
                Sales Trend (Last 30 Days)
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="text-muted-foreground"
              >
                <Eye className="w-4 h-4 mr-2" />
                View Report
              </Button>
            </div>
            <SalesChart data={stats.sales_trend} />
          </div>

          {/* Quick Actions */}
          <div className="bg-card rounded-lg p-6 border">
            <h2 className="text-lg font-semibold text-card-foreground mb-4">
              Quick Actions
            </h2>
            <div className="space-y-3">
              <Button className="w-full justify-start">
                <Plus className="w-4 h-4 mr-3" />
                Add New Product
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Package className="w-4 h-4 mr-3" />
                Manage Inventory
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <ShoppingCart className="w-4 h-4 mr-3" />
                View Orders
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <TrendingUp className="w-4 h-4 mr-3" />
                Sales Report
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Row - Alerts and Best Sellers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stock Alerts */}
          <div className="bg-card rounded-lg p-6 border">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 dark:bg-amber-900 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-lg font-semibold text-card-foreground">
                Stock Alerts
              </h2>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                <div>
                  <p className="font-medium text-card-foreground">
                    Low Stock Items
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Items with quantity ≤ 3
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-amber-600 dark:text-amber-400">
                    {stats.low_stock_items} items
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                <div>
                  <p className="font-medium text-card-foreground">
                    Out of Stock Items
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Items with zero quantity
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-sm font-bold text-red-600 dark:text-red-400">
                    {stats.out_of_stock_items} items
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-card rounded-lg p-6 border">
            <h2 className="text-lg font-semibold text-card-foreground mb-4">
              Recent Activity
            </h2>
            <div className="space-y-3">
              {stats.recent_activity.length > 0 ? (
                stats.recent_activity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-card-foreground text-sm">
                        {activity.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {activity.subtitle}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(activity.amount)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Additional Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {/* Top Categories */}
          <div className="bg-card rounded-lg p-6 border">
            <h2 className="text-lg font-semibold text-card-foreground mb-4">
              Top Categories
            </h2>
            <div className="space-y-3">
              {stats.top_categories.length > 0 ? (
                stats.top_categories.map((category, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-card-foreground">
                        {category.category__name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {category.item_count} items
                      </p>
                    </div>
                    <span className="text-sm font-bold text-green-600 dark:text-green-400">
                      {formatCurrency(category.total_sales)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No category data</p>
              )}
            </div>
          </div>

          {/* Top Customers */}
          <div className="bg-card rounded-lg p-6 border">
            <h2 className="text-lg font-semibold text-card-foreground mb-4">
              Top Customers
            </h2>
            <div className="space-y-3">
              {stats.top_customers.length > 0 ? (
                stats.top_customers.map((customer, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-card-foreground">
                        {customer.customer__name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {customer.invoice_count} invoices
                      </p>
                    </div>
                    <span className="text-sm font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(customer.total_sales)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No customer data</p>
              )}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-card rounded-lg p-6 border">
            <h2 className="text-lg font-semibold text-card-foreground mb-4">
              Payment Methods
            </h2>
            <div className="space-y-3">
              {stats.payment_methods.length > 0 ? (
                stats.payment_methods.map((method, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-card-foreground">
                        {method.payment_method}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {method.count} transactions
                      </p>
                    </div>
                    <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                      {formatCurrency(method.total_amount)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-4">No payment data</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
